/**
 * NavGroups — the four nav sections (Workspace, Learn, Connect, Account).
 *
 * The Assistant nav item's submenu is rendered by `AssistantSubmenu`
 * (composed in the parent Sidebar shell) so this component is a flat
 * list of route buttons with role + access-aware gating.
 *
 * Every nav row is wrapped by the shared `<Tooltip>` primitive when
 * the sidebar is collapsed, replacing the old mix of `title=""` native
 * tooltips + CSS `::after` pseudo-elements with one SSOT implementation.
 *
 * Phase E1: extracted from Sidebar.tsx.
 */

import type { ReactElement } from 'react';
import {
    Bot, Home, BookOpen, UserCheck, Users,
    // Note: re-add `MessageSquare` to this import when un-hiding the
    // Messages tab below (the CollapsedTip block in this file references it).
    Settings, ChevronDown, Lock,
} from 'lucide-react';
import Tooltip from '../../ui/Tooltip';
import { useScrollFade } from '../../ui/useScrollFade';
import { ROUTES, canSeeRoute, resolveRouteLabel } from '../../routing/routes';
import { type BackendUserWithPlan } from '../../../userService';
import type { TabType } from '../../navigation/useNavigationState';

type Props = {
    activeTab: TabType;
    collapsed: boolean;
    userData: BackendUserWithPlan | null;
    networkingSubTab: string;
    assistantExpanded: boolean;
    onTabChange: (tab: TabType, subTab?: string) => void;
    onToggleCollapse: () => void;
    onToggleAssistantMenu: () => void;
    onCollapseAllMenus: () => void;
    onCloseSidebarOnMobile: () => void;

    /** Rendered by the parent when the assistant submenu is open so the
     *  big inline submenu JSX lives close to the nav row. */
    assistantSubmenu: React.ReactNode;
};

/** Wrap a nav button with the shared Tooltip only when the sidebar is
 *  collapsed. Expanded rows already show their label inline. */
function CollapsedTip({
    collapsed, label, children,
}: { collapsed: boolean; label: string; children: ReactElement }) {
    if (!collapsed) return children;
    return (
        <Tooltip label={label} placement="right">
            {children}
        </Tooltip>
    );
}

export default function NavGroups({
    activeTab, collapsed, userData, networkingSubTab,
    assistantExpanded,
    onTabChange, onToggleCollapse, onToggleAssistantMenu,
    onCollapseAllMenus, onCloseSidebarOnMobile,
    assistantSubmenu,
}: Props) {
    const isAdmin = Boolean(userData?.access?.is_admin);
    const { ref: navFadeRef } = useScrollFade<HTMLElement>({
        axis: 'y', fadeSize: 'md',
    });

    const onNav = (tab: TabType, subTab?: string) => {
        onTabChange(tab, subTab);
        onCollapseAllMenus();
        onCloseSidebarOnMobile();
    };

    const mentorshipLabel = resolveRouteLabel(ROUTES.mentorship, userData);

    return (
        <nav ref={navFadeRef} className="sidebar-nav">
            <div className="nav-section">
                {!collapsed && <div className="nav-section-title">Workspace</div>}

                <CollapsedTip collapsed={collapsed} label="Home">
                    <button
                        type="button"
                        className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
                        aria-label="Home"
                        onClick={() => onNav('home')}
                    >
                        <span className="nav-icon"><Home size={18} /></span>
                        {!collapsed && (
                            <div className="nav-content">
                                <span className="nav-label">Home</span>
                            </div>
                        )}
                        {activeTab === 'home' && <span className="active-indicator" />}
                    </button>
                </CollapsedTip>

                <div className="nav-item-with-submenu desktop-submenu">
                    <CollapsedTip collapsed={collapsed} label="Assistant">
                        <button
                            type="button"
                            className={`nav-item ${activeTab === 'assistant' ? 'active' : ''} ${assistantExpanded ? 'expanded' : ''}`}
                            aria-label="Assistant"
                            onClick={() => {
                                if (collapsed) {
                                    onToggleCollapse();
                                    onTabChange('assistant');
                                    return;
                                }
                                if (activeTab === 'assistant') {
                                    onToggleAssistantMenu();
                                } else {
                                    onTabChange('assistant');
                                }
                            }}
                        >
                            <span className="nav-icon"><Bot size={18} /></span>
                            {!collapsed && (
                                <div className="nav-content">
                                    <span className="nav-label">Assistant</span>
                                </div>
                            )}
                            {!collapsed && (
                                <span className={`expand-icon ${assistantExpanded ? 'expanded' : ''}`}>
                                    <ChevronDown size={16} />
                                </span>
                            )}
                            {activeTab === 'assistant' && <span className="active-indicator" />}
                        </button>
                    </CollapsedTip>
                    {!collapsed && assistantExpanded && assistantSubmenu}
                </div>
            </div>

            <div className="nav-section">
                {!collapsed && <div className="nav-section-title">Learn</div>}

                <CollapsedTip collapsed={collapsed} label="Education">
                    <button
                        type="button"
                        className={`nav-item ${activeTab === 'education' ? 'active' : ''}`}
                        aria-label="Education"
                        onClick={() => onNav('education', 'bootcamp')}
                    >
                        <span className="nav-icon"><BookOpen size={18} /></span>
                        {!collapsed && (
                            <div className="nav-content">
                                <span className="nav-label">Education</span>
                                {userData?.access && userData.access.education === false && !isAdmin && (
                                    <Lock size={12} className="nav-lock-icon" />
                                )}
                            </div>
                        )}
                        {activeTab === 'education' && <span className="active-indicator" />}
                    </button>
                </CollapsedTip>

                {canSeeRoute(ROUTES.mentorship, userData) && (
                    <CollapsedTip collapsed={collapsed} label={mentorshipLabel}>
                        <button
                            type="button"
                            className={`nav-item ${activeTab === 'mentorship' ? 'active' : ''}`}
                            aria-label={mentorshipLabel}
                            onClick={() => onNav('mentorship')}
                        >
                            <span className="nav-icon"><UserCheck size={18} /></span>
                            {!collapsed && (
                                <div className="nav-content">
                                    <span className="nav-label">
                                        {mentorshipLabel}
                                    </span>
                                </div>
                            )}
                            {activeTab === 'mentorship' && <span className="active-indicator" />}
                        </button>
                    </CollapsedTip>
                )}
            </div>

            <div className="nav-section">
                {!collapsed && <div className="nav-section-title">Connect</div>}

                <CollapsedTip collapsed={collapsed} label="Networking">
                    <button
                        type="button"
                        className={`nav-item ${activeTab === 'networking' ? 'active' : ''}`}
                        aria-label="Networking"
                        onClick={() => onNav('networking', networkingSubTab || 'connections')}
                    >
                        <span className="nav-icon"><Users size={18} /></span>
                        {!collapsed && (
                            <div className="nav-content">
                                <span className="nav-label">Networking</span>
                                {userData?.access && userData.access.networking === false && !isAdmin && (
                                    <Lock size={12} className="nav-lock-icon" />
                                )}
                            </div>
                        )}
                        {activeTab === 'networking' && <span className="active-indicator" />}
                    </button>
                </CollapsedTip>

                {/* Messages tab hidden — messaging feature is gated off
                 *  for the current rollout. Routing case + module stay
                 *  intact so deep links / notifications don't break;
                 *  only the visible entry point is removed. */}
                {/*
                <CollapsedTip collapsed={collapsed} label="Messages">
                    <button
                        type="button"
                        className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
                        aria-label="Messages"
                        onClick={() => onNav('messages')}
                    >
                        <span className="nav-icon"><MessageSquare size={18} /></span>
                        {!collapsed && (
                            <div className="nav-content">
                                <span className="nav-label">Messages</span>
                            </div>
                        )}
                        {activeTab === 'messages' && <span className="active-indicator" />}
                    </button>
                </CollapsedTip>
                */}
            </div>

            <div className="nav-section">
                {!collapsed && <div className="nav-section-title">Account</div>}

                {/* Admin panes live under Settings → Administration now;
                 *  the separate Admin sidebar row was removed. */}

                <CollapsedTip collapsed={collapsed} label="Settings">
                    <button
                        type="button"
                        className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
                        aria-label="Settings"
                        onClick={() => onNav('settings')}
                    >
                        <span className="nav-icon"><Settings size={18} /></span>
                        {!collapsed && (
                            <div className="nav-content">
                                <span className="nav-label">Settings</span>
                            </div>
                        )}
                        {activeTab === 'settings' && <span className="active-indicator" />}
                    </button>
                </CollapsedTip>
            </div>
        </nav>
    );
}
